import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Buffer } from 'buffer';
import { useFlashMessage } from '../context/FlashMessageContext';
import { API_CONFIG, buildApiUrl } from '../config/api';
import { bufferToHex, base64ToArrayBuffer } from '../utils/biometricEncryption';
import { getOnboardRegistrationErrorMessage } from '../utils/onboardRegistrationErrors';
import { safeDecodeQueryParam } from '../utils/safeDecodeQueryParam';
import { useBiometricEnrollmentMode } from '../hooks/useBiometricEnrollmentMode';
import BiometricCapture from '../components/BiometricCapture';
import BiometricLivenessCapture from '../components/BiometricLivenessCapture';
import './Registration.css';

function Onboard() {
  const [email, setEmail] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  const [livenessSessionId, setLivenessSessionId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError, showInfo } = useFlashMessage();
  const { loading: modeLoading, useLiveness, error: modeError } = useBiometricEnrollmentMode();

  const [callbackUrl, setCallbackUrl] = useState(null);

  useEffect(() => {
    const urlEmail = searchParams.get('email');
    const urlTenantId = searchParams.get('tenant_id');
    const urlCallbackUrl = searchParams.get('callbackurl');

    if (urlEmail) {
      setEmail(safeDecodeQueryParam(urlEmail));
    }
    if (urlTenantId) {
      setTenantId(urlTenantId);
    }
    if (urlCallbackUrl) {
      const decoded = safeDecodeQueryParam(urlCallbackUrl);
      if (decoded.startsWith('http://') || decoded.startsWith('https://')) {
        setCallbackUrl(decoded);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (modeError) {
      showError(modeError);
    }
  }, [modeError, showError]);

  const handleCapture = (imageSrc) => {
    setCapturedImage(imageSrc);
    setLivenessSessionId(null);
    showSuccess('Photo captured successfully! You can now complete the registration.');
  };

  const handleLivenessComplete = (sessionId) => {
    setLivenessSessionId(sessionId);
    setCapturedImage(null);
    showSuccess('Live face verification complete. You can now finish registration.');
  };

  const biometricReady = useLiveness ? Boolean(livenessSessionId) : Boolean(capturedImage);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      showError('Email is required');
      return;
    }

    if (!tenantId.trim()) {
      showError('Tenant ID is required');
      return;
    }

    if (!biometricReady) {
      showError(
        useLiveness
          ? 'Please complete live face verification first'
          : 'Please capture your biometric image first'
      );
      return;
    }

    setIsSubmitting(true);
    showInfo('Processing your biometric data...');

    try {
      const keyResponse = await axios.post(`${API_CONFIG.BASE_URL}/kms/generate-data-key`, {}, {
        timeout: 60000,
      });
      const { plaintext, ciphertext } = keyResponse.data;

      const plaintextKeyBuffer = base64ToArrayBuffer(plaintext);
      const key = await window.crypto.subtle.importKey(
        'raw',
        plaintextKeyBuffer,
        'AES-CBC',
        true,
        ['encrypt', 'decrypt']
      );

      const emailBuffer = new TextEncoder().encode(email);
      const emailIv = window.crypto.getRandomValues(new Uint8Array(16));
      const encryptedEmailBuffer = await window.crypto.subtle.encrypt(
        { name: 'AES-CBC', iv: emailIv },
        key,
        emailBuffer
      );
      const encryptedEmailHex = bufferToHex(encryptedEmailBuffer);
      const emailIvHex = bufferToHex(emailIv);
      const encryptedEmailPayload = `${emailIvHex}:${encryptedEmailHex}`;

      const payload = {
        encryptedDataKey: ciphertext,
        encryptedEmail: encryptedEmailPayload,
        tenantId: tenantId,
      };

      if (useLiveness && livenessSessionId) {
        payload.livenessSessionId = livenessSessionId;
      } else {
        const base64Image = capturedImage.split(',')[1];
        const iv = window.crypto.getRandomValues(new Uint8Array(16));
        const imageBuffer = Buffer.from(base64Image, 'base64');
        const encryptedImageBuffer = await window.crypto.subtle.encrypt(
          { name: 'AES-CBC', iv: iv },
          key,
          imageBuffer
        );
        const encryptedImageHex = bufferToHex(encryptedImageBuffer);
        const ivHex = bufferToHex(iv);
        payload.encryptedImageData = `${ivHex}:${encryptedImageHex}`;
      }

      showInfo('Submitting your data to the server...');

      const response = await axios.post(buildApiUrl(API_CONFIG.ENDPOINTS.REGISTRATION_ONBOARD), payload, {
        timeout: 120000,
      });

      if (response.data.success) {
        showSuccess(
          '🎉 Onboarding successful! Your account has been created with secure biometric authentication.'
        );

        setTimeout(() => {
          showInfo('Redirecting you to the login page...');
          setTimeout(() => {
            navigate('/login', { state: callbackUrl ? { callbackurl: callbackUrl } : undefined });
          }, 2000);
        }, 3000);
      } else {
        showError('Onboarding failed. Please try again.');
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      showError(getOnboardRegistrationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="registration-container">
      <div className="registration-form">
        <h1 className="page-title">Register</h1>
        <h2>{useLiveness ? 'Live Face Verification' : 'Biometric Scan'}</h2>

        <input type="hidden" name="email" value={email} />
        <input type="hidden" name="tenantId" value={tenantId} />

        {email && (
          <div className="form-group">
            <label>Email:</label>
            <input type="text" value={email} disabled />
          </div>
        )}

        {modeLoading ? (
          <p className="biometric-capture-status">Loading verification options…</p>
        ) : useLiveness ? (
          <>
            <BiometricLivenessCapture
              onComplete={handleLivenessComplete}
              onError={(msg) => showError(msg)}
            />
            {livenessSessionId && (
              <p className="biometric-capture-status">Live verification complete.</p>
            )}
          </>
        ) : (
          <>
            <BiometricCapture mode="capture" onCapture={handleCapture} />
            {capturedImage && (
              <div className="webcam-preview">
                <h3>Captured Image Preview</h3>
                <img src={capturedImage} alt="Captured" />
              </div>
            )}
          </>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!biometricReady || isSubmitting || modeLoading}
          className={`registration-form__primary${isSubmitting ? ' loading' : ''}`}
        >
          {isSubmitting ? 'Creating Account...' : 'Complete Registration'}
        </button>
      </div>
    </div>
  );
}

export default Onboard;
